import type { UppKonvaAreaSource } from '@unpispas/upp-konva';
import type { UppKonvaModel } from '@unpispas/upp-konva';
import type { UppKonvaShapeKind } from '@unpispas/upp-konva';
import type { UppKonvaShapeSource } from '@unpispas/upp-konva';

import { DrawItem } from '@unpispas/upp-data';
import { FloorArea } from '@unpispas/upp-data';

import { UppFloorUtils } from '../common/upp-area';
import { UppShapeUtils } from '../edit/shapes/common/upp-utils';

export type KonvaLabels = (draw: DrawItem) => { label?: string; sublabel?: string };

/** Konva projection: {@link FloorArea} → {@link UppKonvaAreaSource}. */
export class UppCommon {
    private static shapeFromDraw(draw: DrawItem, kind: UppKonvaShapeKind): Record<string, unknown> {
        return {
            ...(draw._uuid !== '' ? { id: draw._uuid } : {}),
            kind,
            x: Number.isFinite(Number(draw.coord_x)) ? Number(draw.coord_x) : 0,
            y: Number.isFinite(Number(draw.coord_y)) ? Number(draw.coord_y) : 0,
            rotation: UppShapeUtils.Rotation360(Number(draw.coord_r ?? 0)),
            width: Number.isFinite(Number(draw.coord_w)) ? Number(draw.coord_w) : 0,
            height: Number.isFinite(Number(draw.coord_h)) ? Number(draw.coord_h) : 0,
        };
    }

    private static vertexFromDraw(item: DrawItem): Record<string, unknown> {
        const sortN = Number(item.sort);
        const sort = Number.isFinite(sortN) ? sortN : 0;
        const poligon = typeof item.shape === 'string' ? item.shape : '';
        return { sort, poligon, closed: true };
    }

    private static drawsToSources(area: FloorArea, tableLabels: KonvaLabels): UppKonvaShapeSource[] {
        const shapes: UppKonvaShapeSource[] = [];

        for (const draw of area.draws) {
            const _kind = draw.type as UppKonvaShapeKind | null;

            if (!draw.IsValid || !_kind) {
                continue;
            }

            let _shape: Record<string, unknown>;
            switch (String(draw.type ?? '').trim().toUpperCase()) {
                case 'VERTEX':
                    _shape = UppCommon.vertexFromDraw(draw);
                    break;
                case 'TABLE':
                    _shape = UppFloorUtils.TableFromDraw(draw);
                    break;
                default:
                    _shape = UppFloorUtils.OtherFromDraw(draw);
                    break;
            }

            const row: UppKonvaShapeSource = {
                kind: _kind,
                ...UppCommon.shapeFromDraw(draw, _kind),
                data: _shape,
            };

            if (_kind === 'TABLE') {
                Object.assign(row, tableLabels(draw));
            }
            shapes.push(row);
        }

        return shapes;
    }

    /**
     * Builds a Konva area source from {@link FloorArea} (same projection as floor edit).
     */
    static AreaToKonva(area: FloorArea, konva: UppKonvaModel | null, mode: 'VERTEX' | 'SHAPES', tableLabels: KonvaLabels): UppKonvaAreaSource {
        const d = 1000;
        const w = Math.abs(Number(area.coord_w || d));
        const h = Math.abs(Number(area.coord_h || d));
        const name = area.name || '';

        return {
            mode,
            name,
            orientation: konva?.area?.orientation ?? 'AUTO',
            width: w,
            height: h,
            shapes: UppCommon.drawsToSources(area, tableLabels),
        };
    }

    /** Empty room for VIEW when no {@link FloorArea} is bound (keeps Konva warm without shapes). */
    static EmptyViewArea(konva: UppKonvaModel | null): UppKonvaAreaSource {
        const d = 1000;
        return {
            mode: 'SHAPES',
            name: '',
            orientation: konva?.area?.orientation ?? 'AUTO',
            width: d,
            height: d,
            shapes: [],
        };
    }
}
